import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Spin, Tabs, Typography, message } from 'antd';
import {
  AppstoreOutlined,
  BarChartOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HomeFilled,
  PieChartOutlined,
  ReloadOutlined,
  StockOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import moment from 'moment';
import configService from '../services/configService';
import { getDividendData } from '../services/dividendService';

const { Text } = Typography;
const { TabPane } = Tabs;

const MOBILE_PORTFOLIO_CACHE_KEY = 'mobilePortfolioCache';
const MOBILE_PORTFOLIO_CACHE_TTL_MS = 5 * 60 * 1000;

const SORT_OPTIONS = [
  { key: 'evaluation', label: '평가금액순' },
  { key: 'today', label: '당일 수익순' },
  { key: 'total', label: '누적 수익순' }
];

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCurrency = (amount, hideAmounts = false) => {
  if (hideAmounts) return '******';
  return `${Math.round(numberValue(amount)).toLocaleString('ko-KR')}원`;
};

const formatSignedCurrency = (amount, hideAmounts = false) => {
  if (hideAmounts) return '******';
  const value = Math.round(numberValue(amount));
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('ko-KR')}원`;
};

const formatPercent = (value) => {
  const parsed = numberValue(value);
  const sign = parsed > 0 ? '+' : '';
  return `${sign}${parsed.toFixed(2)}%`;
};

const getPnLClassName = (value) => {
  const parsed = numberValue(value);
  if (parsed > 0) return 'mobile-positive';
  if (parsed < 0) return 'mobile-negative';
  return 'mobile-neutral';
};

const getDefaultDividendMonth = () => moment().format('YYYY-MM');

const formatLoadedAt = (timestamp) => {
  if (!timestamp) return '아직 로딩 전';
  return moment(timestamp).format('YYYY.MM.DD HH:mm');
};

const getAccountLabel = (account = {}, fallback = '') => {
  const source = account.accountName || account.description || account.brokerName || account.broker || fallback || '계좌';
  const compact = String(source).replace(/\s+/g, '');
  const lower = compact.toLowerCase();
  const broker = String(account.broker || '').toLowerCase();
  const accountType = String(account.accountType || '').toLowerCase();
  const isKis = compact.includes('한국') || compact.includes('한투') || lower.includes('kis') || broker.includes('kis');
  const isNh = compact.includes('NH') || compact.includes('나무') || lower.includes('nh') || broker.includes('nh');
  const isKiwoom = compact.includes('키움') || lower.includes('kiwoom') || broker.includes('kiwoom');

  if (isKis && (compact.includes('ISA') || accountType.includes('isa'))) return '한투ISA';
  if (isKis && (compact.includes('IRP') || compact.includes('퇴직') || accountType.includes('irp'))) return '한투IRP';
  if (isKis && (compact.includes('연금') || accountType.includes('pension'))) return '한투연금';

  if (isNh && (compact.includes('DC') || accountType.includes('dc'))) return 'NHDC';
  if (isNh && (compact.includes('개인연금') || compact.includes('연금') || accountType.includes('pension'))) return 'NH연금';
  if (isNh && (compact.includes('ISA') || accountType.includes('isa'))) return 'NHISA';

  if (isKis) return '한투';
  if (isKiwoom) return '키움';
  if (isNh) return 'NH';
  if (compact.includes('연금')) return '연금';
  if (compact.includes('ISA')) return 'ISA';

  return compact.slice(0, 4).toUpperCase();
};

const getAccountColor = (account = {}) => account.color || '#2563eb';

const createEmptyAccountData = () => ({ holdings: [] });

const MobilePortfolio = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountsData, setAccountsData] = useState({});
  const [accountLoadingStates, setAccountLoadingStates] = useState({});
  const [dividends, setDividends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [selectedDividendMonth, setSelectedDividendMonth] = useState(getDefaultDividendMonth);
  const [selectedAccountId, setSelectedAccountId] = useState('all');
  const [sortMode, setSortMode] = useState('evaluation');
  const [hideAmounts, setHideAmounts] = useState(() => localStorage.getItem('hideAmounts') === 'true');

  const applyCachedData = useCallback((cachedData) => {
    setAccounts(cachedData.accounts || []);
    setAccountsData(cachedData.accountsData || {});
    setDividends(cachedData.dividends || []);
    setLoadedAt(cachedData.loadedAt || cachedData.cachedAt || null);
    setAccountLoadingStates({});
  }, []);

  const loadFromCache = useCallback(() => {
    try {
      const rawCache = localStorage.getItem(MOBILE_PORTFOLIO_CACHE_KEY);
      if (!rawCache) return false;

      const cachedData = JSON.parse(rawCache);
      const cachedAt = cachedData.cachedAt ? new Date(cachedData.cachedAt).getTime() : 0;
      const isFresh = cachedAt && Date.now() - cachedAt < MOBILE_PORTFOLIO_CACHE_TTL_MS;

      if (!isFresh) return false;

      applyCachedData(cachedData);
      return true;
    } catch (cacheError) {
      console.warn('모바일 포트폴리오 캐시 로드 실패:', cacheError);
      localStorage.removeItem(MOBILE_PORTFOLIO_CACHE_KEY);
      return false;
    }
  }, [applyCachedData]);

  const saveToCache = useCallback((nextData) => {
    try {
      localStorage.setItem(MOBILE_PORTFOLIO_CACHE_KEY, JSON.stringify(nextData));
    } catch (cacheError) {
      console.warn('모바일 포트폴리오 캐시 저장 실패:', cacheError);
    }
  }, []);

  const loadData = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
    try {
      if (!forceRefresh && loadFromCache()) {
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      await configService.loadConfig();
      const activeAccounts = configService.getAllAccounts();
      setAccounts(activeAccounts);
      setAccountsData({});
      setAccountLoadingStates(
        activeAccounts.reduce((acc, account) => ({ ...acc, [account.accountId]: true }), {})
      );
      setLoading(false);

      const dividendPromise = getDividendData({ page_size: 100 })
        .then((dividendResponse) => {
          const nextDividends = dividendResponse?.results || [];
          setDividends(nextDividends);
          return nextDividends;
        })
        .catch((dividendError) => {
          console.warn('배당 데이터 로드 실패:', dividendError);
          return [];
        });

      const nextAccountsData = {};

      for (const account of activeAccounts) {
        try {
          const accountRequest = [{
            account_id: account.accountId,
            broker: account.broker,
            account_number: account.accountNumber
          }];
          const balanceResponse = await configService.getMultipleAccountBalances(accountRequest, !forceRefresh);
          const accountData = balanceResponse?.data?.results?.[account.accountId] || createEmptyAccountData();

          nextAccountsData[account.accountId] = accountData;
          setAccountsData((prev) => ({ ...prev, [account.accountId]: accountData }));
        } catch (accountError) {
          console.warn(`모바일 계좌 데이터 로드 실패 (${account.accountName}):`, accountError);
          const failedData = { ...createEmptyAccountData(), error: accountError.message || '계좌 조회 실패' };
          nextAccountsData[account.accountId] = failedData;
          setAccountsData((prev) => ({ ...prev, [account.accountId]: failedData }));
        } finally {
          setAccountLoadingStates((prev) => ({ ...prev, [account.accountId]: false }));
        }
      }

      const nextDividends = await dividendPromise;
      const nextLoadedAt = new Date().toISOString();
      setLoadedAt(nextLoadedAt);
      saveToCache({
        accounts: activeAccounts,
        accountsData: nextAccountsData,
        dividends: nextDividends,
        loadedAt: nextLoadedAt,
        cachedAt: nextLoadedAt
      });
    } catch (loadError) {
      console.error('모바일 포트폴리오 데이터 로드 실패:', loadError);
      setError('모바일 자산 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadFromCache, saveToCache]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleHideAmounts = useCallback(() => {
    setHideAmounts((prev) => {
      const next = !prev;
      localStorage.setItem('hideAmounts', String(next));
      return next;
    });
  }, []);

  const handleRefresh = useCallback(async () => {
    localStorage.removeItem(MOBILE_PORTFOLIO_CACHE_KEY);
    await loadData({ silent: true, forceRefresh: true });
    message.success('모바일 자산 정보를 새로고침했습니다.');
  }, [loadData]);

  const accountById = useMemo(() => {
    const map = new Map();
    accounts.forEach((account) => map.set(account.accountId, account));
    return map;
  }, [accounts]);

  const accountSummaries = useMemo(() => accounts.map((account) => {
    const accountData = accountsData[account.accountId] || {};
    const accountHoldings = Array.isArray(accountData.holdings) ? accountData.holdings : [];
    const holdingsEvaluation = accountHoldings.reduce((sum, holding) => sum + numberValue(
      holding.evaluation_amount || holding.market_value || holding.eval_amount
    ), 0);
    const holdingsProfitLoss = accountHoldings.reduce((sum, holding) => sum + numberValue(
      holding.profit_loss || holding.unrealized_pnl
    ), 0);
    const todayPnL = accountHoldings.reduce((sum, holding) => {
      const quantity = numberValue(holding.quantity || holding.qty || holding.hldg_qty);
      const todayChangePerShare = numberValue(holding.prev_day_change, null);
      return sum + (todayChangePerShare === null ? 0 : todayChangePerShare * quantity);
    }, 0);

    return {
      account,
      label: getAccountLabel(account),
      totalAssets: numberValue(accountData.total_balance || accountData.total_assets) || holdingsEvaluation,
      holdingsEvaluation,
      totalProfitLoss: numberValue(accountData.total_profit_loss || accountData.total_unrealized_pnl) || holdingsProfitLoss,
      todayPnL,
      isLoading: !!accountLoadingStates[account.accountId],
      hasError: !!accountData.error
    };
  }), [accounts, accountsData, accountLoadingStates]);

  const rawHoldingRows = useMemo(() => {
    const rows = [];

    accounts.forEach((account) => {
      const accountData = accountsData[account.accountId] || {};
      const accountHoldings = Array.isArray(accountData.holdings) ? accountData.holdings : [];

      accountHoldings.forEach((holding) => {
        const symbol = holding.symbol || holding.stock_symbol || holding.code || holding.name;
        if (!symbol) return;

        const quantity = numberValue(holding.quantity || holding.qty || holding.hldg_qty);
        const evaluationAmount = numberValue(holding.evaluation_amount || holding.market_value || holding.eval_amount);
        const profitLoss = numberValue(holding.profit_loss || holding.unrealized_pnl);
        const todayChangePerShare = numberValue(holding.prev_day_change, null);
        const todayPnL = todayChangePerShare === null ? 0 : todayChangePerShare * quantity;
        const avgBuyPrice = numberValue(holding.avg_buy_price || holding.average_price || holding.pchs_avg_pric);
        const profitLossPercent = holding.profit_loss_percent !== undefined && holding.profit_loss_percent !== null
          ? numberValue(holding.profit_loss_percent)
          : (evaluationAmount - profitLoss > 0 ? (profitLoss / (evaluationAmount - profitLoss)) * 100 : 0);

        rows.push({
          symbol,
          name: holding.name || holding.stock_name || holding.product_name || symbol,
          quantity,
          evaluationAmount,
          profitLoss,
          profitLossPercent,
          todayPnL,
          avgBuyPrice,
          accountId: account.accountId,
          accountName: account.accountName,
          accountLabel: getAccountLabel(account),
          accountColor: getAccountColor(account)
        });
      });
    });

    return rows;
  }, [accounts, accountsData]);

  const holdings = useMemo(() => {
    const rows = selectedAccountId === 'all'
      ? rawHoldingRows
      : rawHoldingRows.filter((holding) => holding.accountId === selectedAccountId);

    const mergedMap = new Map();
    rows.forEach((holding) => {
      const mergeKey = selectedAccountId === 'all' ? holding.symbol : `${holding.accountId}:${holding.symbol}`;

      if (mergedMap.has(mergeKey)) {
        const existing = mergedMap.get(mergeKey);
        const nextEvaluationAmount = existing.evaluationAmount + holding.evaluationAmount;
        const nextProfitLoss = existing.profitLoss + holding.profitLoss;
        const nextCost = Math.max(0, nextEvaluationAmount - nextProfitLoss);
        const accountLabels = [...new Set([...existing.accountLabels, holding.accountLabel])];
        const accountNames = [...new Set([...existing.accountNames, holding.accountName])];

        mergedMap.set(mergeKey, {
          ...existing,
          quantity: existing.quantity + holding.quantity,
          evaluationAmount: nextEvaluationAmount,
          profitLoss: nextProfitLoss,
          todayPnL: existing.todayPnL + holding.todayPnL,
          accountLabels,
          accountNames,
          accountBadge: accountLabels.length > 1 ? `${accountLabels[0]}+${accountLabels.length - 1}` : accountLabels[0],
          profitLossPercent: nextCost > 0 ? (nextProfitLoss / nextCost) * 100 : 0
        });
      } else {
        mergedMap.set(mergeKey, {
          ...holding,
          accountLabels: [holding.accountLabel],
          accountNames: [holding.accountName],
          accountBadge: holding.accountLabel
        });
      }
    });

    const sorted = Array.from(mergedMap.values());
    sorted.sort((a, b) => {
      if (sortMode === 'today') return b.todayPnL - a.todayPnL;
      if (sortMode === 'total') return b.profitLoss - a.profitLoss;
      return b.evaluationAmount - a.evaluationAmount;
    });
    return sorted;
  }, [rawHoldingRows, selectedAccountId, sortMode]);

  const summary = useMemo(() => {
    const selectedSummary = accountSummaries.find((item) => item.account.accountId === selectedAccountId);

    if (selectedSummary) {
      const totalCost = Math.max(0, selectedSummary.holdingsEvaluation - selectedSummary.totalProfitLoss);
      return {
        totalAssets: selectedSummary.totalAssets,
        holdingsEvaluation: selectedSummary.holdingsEvaluation,
        totalProfitLoss: selectedSummary.totalProfitLoss,
        totalProfitLossPercent: totalCost > 0 ? (selectedSummary.totalProfitLoss / totalCost) * 100 : 0,
        todayPnL: selectedSummary.todayPnL,
        stockCount: holdings.length
      };
    }

    const totalAssets = accountSummaries.reduce((sum, item) => sum + item.totalAssets, 0);
    const holdingsEvaluation = accountSummaries.reduce((sum, item) => sum + item.holdingsEvaluation, 0);
    const totalProfitLoss = accountSummaries.reduce((sum, item) => sum + item.totalProfitLoss, 0);
    const todayPnL = accountSummaries.reduce((sum, item) => sum + item.todayPnL, 0);
    const totalCost = Math.max(0, holdingsEvaluation - totalProfitLoss);

    return {
      totalAssets,
      holdingsEvaluation,
      totalProfitLoss,
      totalProfitLossPercent: totalCost > 0 ? (totalProfitLoss / totalCost) * 100 : 0,
      todayPnL,
      stockCount: holdings.length
    };
  }, [accountSummaries, holdings.length, selectedAccountId]);

  const dividendSummary = useMemo(() => {
    const monthlyMap = new Map();
    const normalizedDividends = dividends.map((dividend) => {
      const amountPerShare = numberValue(dividend.dividend_amount);
      const quantity = numberValue(dividend.quantity);
      const totalAmount = amountPerShare * quantity;
      const monthKey = dividend.dividend_date ? moment(dividend.dividend_date).format('YYYY-MM') : '날짜 없음';
      const matchedAccount = accounts.find((account) => account.accountName === dividend.account_name);
      const accountLabel = getAccountLabel(matchedAccount, dividend.account_name);

      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + totalAmount);

      return {
        ...dividend,
        amountPerShare,
        quantity,
        totalAmount,
        monthKey,
        accountLabel,
        accountColor: getAccountColor(matchedAccount)
      };
    });

    const monthly = Array.from(monthlyMap.entries())
      .map(([month, total]) => ({ month, total }))
      .sort((a, b) => b.month.localeCompare(a.month));
    const currentMonth = getDefaultDividendMonth();
    const hasCurrentMonth = monthly.some((item) => item.month === currentMonth);
    const monthlyCards = hasCurrentMonth ? monthly : [{ month: currentMonth, total: 0 }, ...monthly];
    const selectedMonthDividends = normalizedDividends
      .filter((dividend) => dividend.monthKey === selectedDividendMonth)
      .sort((a, b) => moment(b.dividend_date).valueOf() - moment(a.dividend_date).valueOf());
    const selectedMonthTotal = selectedMonthDividends.reduce((sum, dividend) => sum + dividend.totalAmount, 0);

    return {
      selectedMonthTotal,
      selectedMonthDividends,
      monthly: monthlyCards.slice(0, 8)
    };
  }, [accounts, dividends, selectedDividendMonth]);

  const isAnyAccountLoading = Object.values(accountLoadingStates).some(Boolean);

  if (loading) {
    return (
      <div className="mobile-portfolio-page mobile-loading-page">
        <Spin size="large" />
        <Text className="mobile-loading-text">계좌 설정을 불러오는 중입니다</Text>
      </div>
    );
  }

  return (
    <div className="mobile-portfolio-page">
      <header className="mobile-topbar">
        <div className="mobile-brand">
          <span className="mobile-brand-mark"><BarChartOutlined /></span>
          <span>총 자산</span>
        </div>
        <div className="mobile-top-actions">
          <Button
            type="text"
            shape="circle"
            icon={hideAmounts ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={toggleHideAmounts}
            aria-label="자산 금액 가리기"
          />
          <Button
            type="text"
            shape="circle"
            icon={<ReloadOutlined spin={refreshing || isAnyAccountLoading} />}
            onClick={handleRefresh}
            aria-label="새로고침"
          />
        </div>
      </header>

      {error && <Alert className="mobile-alert" type="warning" showIcon message={error} />}

      <section className="mobile-hero-card">
        <div className="mobile-hero-label">
          {selectedAccountId === 'all'
            ? '내 포트폴리오'
            : accountById.get(selectedAccountId)?.accountName || '선택 계좌'}
        </div>
        <div className="mobile-total-assets">{formatCurrency(summary.totalAssets, hideAmounts)}</div>
        <div className={`mobile-today-pnl ${getPnLClassName(summary.todayPnL)}`}>
          {formatSignedCurrency(summary.todayPnL, hideAmounts)} 금일 손익
        </div>
        <div className="mobile-loaded-at">
          데이터 로딩 시간 {formatLoadedAt(loadedAt)}
          {isAnyAccountLoading ? ' · 계좌별 로딩 중' : ''}
        </div>

        <div className="mobile-summary-grid">
          <div>
            <span>누적 손익</span>
            <strong className={getPnLClassName(summary.totalProfitLoss)}>
              {formatSignedCurrency(summary.totalProfitLoss, hideAmounts)}
            </strong>
            <small className={getPnLClassName(summary.totalProfitLossPercent)}>
              {formatPercent(summary.totalProfitLossPercent)}
            </small>
          </div>
          <div>
            <span>평가금액</span>
            <strong>{formatCurrency(summary.holdingsEvaluation, hideAmounts)}</strong>
            <small>{summary.stockCount}개 종목</small>
          </div>
          <div>
            <span>계좌</span>
            <strong>{selectedAccountId === 'all' ? `${accounts.length}개` : getAccountLabel(accountById.get(selectedAccountId))}</strong>
            <small>{isAnyAccountLoading ? '로딩 중' : '조회 완료'}</small>
          </div>
        </div>

        <div className="mobile-account-filter-row">
          <button
            type="button"
            className={selectedAccountId === 'all' ? 'active' : ''}
            onClick={() => setSelectedAccountId('all')}
          >
            <span>전체</span>
            <strong>{formatCurrency(accountSummaries.reduce((sum, item) => sum + item.totalAssets, 0), hideAmounts)}</strong>
          </button>
          {accountSummaries.map(({ account, label, totalAssets, totalProfitLoss, isLoading, hasError }) => (
            <button
              type="button"
              key={account.accountId}
              className={selectedAccountId === account.accountId ? 'active' : ''}
              onClick={() => setSelectedAccountId(account.accountId)}
            >
              <span>{label} {isLoading ? '불러오는 중' : hasError ? '오류' : ''}</span>
              <em>{account.accountName || account.description || account.brokerName}</em>
              <strong>{isLoading ? '...' : formatCurrency(totalAssets, hideAmounts)}</strong>
              {!isLoading && <small className={getPnLClassName(totalProfitLoss)}>{formatSignedCurrency(totalProfitLoss, hideAmounts)}</small>}
            </button>
          ))}
        </div>
      </section>

      <nav className="mobile-shortcut-row" aria-label="모바일 포트폴리오 빠른 메뉴">
        <a href="#mobile-profit-section"><BarChartOutlined /><span>수익</span></a>
        <a href="#mobile-dividend-section"><TrophyOutlined /><span>배당</span></a>
        <a href="#mobile-holdings-section"><StockOutlined /><span>종목</span></a>
        <a href="/dashboard"><HomeFilled /><span>홈</span></a>
      </nav>

      <main className="mobile-content">
        <section id="mobile-profit-section" className="mobile-section">
          <div className="mobile-section-title">
            <h2>투자</h2>
            <span>{summary.stockCount}개</span>
          </div>

          <div className="mobile-sort-row">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={sortMode === option.key ? 'active' : ''}
                onClick={() => setSortMode(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Tabs defaultActiveKey="profit" className="mobile-tabs">
            <TabPane tab={<span><BarChartOutlined /> 수익</span>} key="profit">
              <div id="mobile-holdings-section" className="mobile-holding-list">
                {holdings.length > 0 ? holdings.map((holding) => (
                  <article className="mobile-holding-item" key={`${holding.symbol}-${holding.accountBadge}`}>
                    <div
                      className="mobile-stock-badge mobile-account-badge"
                      style={{ background: holding.accountColor || '#2563eb' }}
                    >
                      {holding.accountBadge}
                    </div>
                    <div className="mobile-stock-info">
                      <strong>{holding.name}</strong>
                      <span>{holding.quantity.toLocaleString('ko-KR')}주 · {holding.symbol}</span>
                      {holding.accountNames.length > 0 && <small>{holding.accountNames.join(', ')}</small>}
                    </div>
                    <div className="mobile-stock-values">
                      <strong>{formatCurrency(holding.evaluationAmount, hideAmounts)}</strong>
                      <span className={getPnLClassName(holding.todayPnL)}>
                        오늘 {formatSignedCurrency(holding.todayPnL, hideAmounts)}
                      </span>
                      <span className={getPnLClassName(holding.profitLoss)}>
                        누적 {formatSignedCurrency(holding.profitLoss, hideAmounts)} ({formatPercent(holding.profitLossPercent)})
                      </span>
                    </div>
                  </article>
                )) : (
                  <Empty description={isAnyAccountLoading ? '계좌별 보유 종목을 불러오는 중입니다' : '표시할 보유 종목이 없습니다'} />
                )}
              </div>
            </TabPane>
            <TabPane tab={<span><PieChartOutlined /> 비중</span>} key="weight">
              <div className="mobile-weight-list">
                {holdings.length > 0 ? holdings.map((holding) => {
                  const weight = summary.holdingsEvaluation > 0
                    ? (holding.evaluationAmount / summary.holdingsEvaluation) * 100
                    : 0;

                  return (
                    <div className="mobile-weight-item" key={`${holding.symbol}-${holding.accountBadge}`}>
                      <div>
                        <strong>{holding.name}</strong>
                        <span>{holding.accountBadge} · {formatCurrency(holding.evaluationAmount, hideAmounts)}</span>
                      </div>
                      <div className="mobile-weight-bar">
                        <span style={{ width: `${Math.min(weight, 100)}%` }} />
                      </div>
                      <em>{weight.toFixed(1)}%</em>
                    </div>
                  );
                }) : (
                  <Empty description="비중을 계산할 보유 종목이 없습니다" />
                )}
              </div>
            </TabPane>
          </Tabs>
        </section>

        <section id="mobile-dividend-section" className="mobile-section mobile-dividend-section">
          <div className="mobile-section-title">
            <h2>배당금 히스토리</h2>
            <span>{selectedDividendMonth}</span>
          </div>

          <div className="mobile-dividend-total-card">
            <div>
              <span>{selectedDividendMonth} 배당금</span>
              <strong>{formatCurrency(dividendSummary.selectedMonthTotal, hideAmounts)}</strong>
            </div>
            <TrophyOutlined />
          </div>

          {dividendSummary.monthly.length > 0 && (
            <div className="mobile-monthly-dividends">
              {dividendSummary.monthly.map((item) => (
                <button
                  type="button"
                  key={item.month}
                  className={item.month === selectedDividendMonth ? 'active' : ''}
                  onClick={() => setSelectedDividendMonth(item.month)}
                >
                  <span>{item.month}</span>
                  <strong>{formatCurrency(item.total, hideAmounts)}</strong>
                </button>
              ))}
            </div>
          )}

          <div className="mobile-dividend-list">
            {dividendSummary.selectedMonthDividends.length > 0 ? dividendSummary.selectedMonthDividends.map((dividend, index) => (
              <article className="mobile-dividend-item" key={`${dividend.id || dividend.stock_symbol}-${dividend.dividend_date}-${index}`}>
                <div
                  className="mobile-dividend-icon mobile-account-badge"
                  style={{ background: dividend.accountColor || '#16a34a' }}
                >
                  {dividend.accountLabel}
                </div>
                <div>
                  <strong>{dividend.stock_name || dividend.stock_symbol}</strong>
                  <span>
                    {dividend.dividend_date ? moment(dividend.dividend_date).format('YYYY.MM.DD') : '날짜 없음'}
                    {' · '}{dividend.quantity.toLocaleString('ko-KR')}주
                  </span>
                  <small>{dividend.account_name || '계좌 정보 없음'} · 주당 {formatCurrency(dividend.amountPerShare, hideAmounts)}</small>
                </div>
                <strong>{formatCurrency(dividend.totalAmount, hideAmounts)}</strong>
              </article>
            )) : (
              <Empty description={`${selectedDividendMonth} 배당 내역이 없습니다`} />
            )}
          </div>
        </section>
      </main>

      <footer className="mobile-bottom-nav">
        <a href="#mobile-profit-section" className="active"><HomeFilled /><span>자산</span></a>
        <a href="#mobile-holdings-section"><AppstoreOutlined /><span>종목</span></a>
        <a href="#mobile-dividend-section"><TrophyOutlined /><span>배당</span></a>
        <button type="button" onClick={handleRefresh}><ReloadOutlined spin={refreshing || isAnyAccountLoading} /><span>새로고침</span></button>
      </footer>
    </div>
  );
};

export default MobilePortfolio;
